/**
 * Builder Mode homebrew overlay.
 *
 * Builder Mode rebuilds an existing sheet from static SRD data and so never saw
 * session homebrew — a character on a homebrew class/race couldn't be rebuilt
 * (the class/race wasn't listed and didn't resolve). These helpers merge the
 * session's homebrew packages into the base SRD lists, reusing the SAME shared
 * class mapper as the server overlay / level-up path so a homebrew class carries
 * merged proficiencies and derived spell slots consistently.
 */

import { useMemo } from 'react';
import { srdClasses } from '@roll1/shared/srd/classes';
import { srdRaces } from '@roll1/shared/srd/races';
import { homebrewClassDataToSrdClass } from '@roll1/shared/srd/homebrew-class-mapper';
import {
  foldHomebrewSubclassFeatures,
  slugify,
  type HbSubclassEntry,
} from '@roll1/shared/systems/dnd5e/srd/homebrew-subclass.js';
import { srdSpellMap, srdEquipmentMap } from '@roll1/shared/systems/dnd5e/srd/index.js';
import type { SrdClass, SrdRace } from '@roll1/shared/srd/types';
import type { SrdSpell, SrdEquipmentItem } from '@roll1/shared/systems/dnd5e/srd/types.js';
import type { HomebrewSessionPackage } from '@roll1/shared';
import { useOptionalSessionContext } from '../../../contexts/SessionContext';

/**
 * Base SRD classes + connected homebrew classes (mapped to the engine shape),
 * with homebrew subclass features folded into their parent class.
 *
 * The SRD class constants share the global feature arrays, so each SRD class is
 * shallow-cloned before folding: `foldHomebrewSubclassFeatures` reassigns a NEW
 * `features` array on the clone, leaving the shared SRD constants' properties
 * untouched. Homebrew "subclass" entries are collected into `HbSubclassEntry[]`
 * and folded in one pass, matching the server overlay so the client engine sees
 * the same merged classes.
 */
export function mergedBuilderClasses(
  homebrewPackages: HomebrewSessionPackage[] | undefined,
): SrdClass[] {
  const out: SrdClass[] = srdClasses.map((c: SrdClass): SrdClass => ({ ...c }));
  const subclassEntries: HbSubclassEntry[] = [];
  for (const pkg of homebrewPackages ?? []) {
    for (const e of pkg.entries) {
      if (e.type === 'class') {
        out.push(homebrewClassDataToSrdClass(e.slug, e.name, e.data));
      } else if (e.type === 'subclass') {
        subclassEntries.push({ slug: e.slug, name: e.name, data: e.data });
      }
    }
  }
  foldHomebrewSubclassFeatures(out, subclassEntries);
  return out;
}

/** A selectable subclass in Builder Mode, tagged with its parent class slug. */
export interface BuilderSubclassOption {
  slug: string;
  name: string;
  parentClass: string;
}

/**
 * Base SRD subclasses + connected homebrew subclasses. Each SRD class exposes at
 * most one subclass (`srdClass.subclass`), keyed by `slugify(name)`; homebrew
 * "subclass" entries carry their parent class slug in `data.parentClass`. When
 * `parentClassSlug` is given, the result is narrowed to that class. Mirrors
 * `mergedBuilderRaces` in style.
 */
export function mergedBuilderSubclasses(
  homebrewPackages: HomebrewSessionPackage[] | undefined,
  parentClassSlug?: string,
): BuilderSubclassOption[] {
  const out: BuilderSubclassOption[] = [];
  for (const cls of srdClasses) {
    if (cls.subclass) {
      out.push({
        slug: slugify(cls.subclass.name),
        name: cls.subclass.name,
        parentClass: cls.slug,
      });
    }
  }
  for (const pkg of homebrewPackages ?? []) {
    for (const e of pkg.entries) {
      if (e.type === 'subclass') {
        out.push({
          slug: e.slug,
          name: e.name,
          parentClass: (e.data.parentClass as string) ?? '',
        });
      }
    }
  }
  return parentClassSlug
    ? out.filter((s: BuilderSubclassOption): boolean => s.parentClass === parentClassSlug)
    : out;
}

/** Base SRD races + connected homebrew races (mirrors server session-srd getRaces). */
export function mergedBuilderRaces(
  homebrewPackages: HomebrewSessionPackage[] | undefined,
): SrdRace[] {
  const out: SrdRace[] = [...srdRaces];
  for (const pkg of homebrewPackages ?? []) {
    for (const e of pkg.entries) {
      if (e.type === 'race') {
        out.push({
          slug: e.slug,
          name: e.name,
          speed: (e.data.speed as number) ?? 30,
          size: (e.data.size as string) ?? 'Medium',
          abilityBonuses: (e.data.abilityBonuses as SrdRace['abilityBonuses']) ?? [],
          alignment: (e.data.alignment as string) ?? '',
          age: (e.data.age as string) ?? '',
          sizeDescription: (e.data.sizeDescription as string) ?? '',
          languages: (e.data.languages as string[]) ?? [],
          languageDescription: (e.data.languageDescription as string) ?? '',
          traits: (e.data.traits as SrdRace['traits']) ?? [],
          subraces: (e.data.subraces as SrdRace['subraces']) ?? [],
        });
      }
    }
  }
  return out;
}

/**
 * Base SRD spells + connected homebrew spells, indexed by slug. Mirrors the
 * server session-srd getSpells mapping so the HUD sheet adapter can enrich
 * homebrew spellbook entries with school/level/components/etc.
 */
export function mergedSpellMap(pkgs: HomebrewSessionPackage[]): Map<string, SrdSpell> {
  const out = new Map<string, SrdSpell>(srdSpellMap);
  for (const pkg of pkgs) {
    for (const e of pkg.entries) {
      if (e.type === 'spell') {
        out.set(e.slug, {
          slug: e.slug,
          name: e.name,
          level: (e.data.level as number) ?? 0,
          school: (e.data.school as SrdSpell['school']) ?? 'Evocation',
          castingTime: (e.data.castingTime as string) ?? '1 action',
          range: (e.data.range as string) ?? 'Self',
          components: (e.data.components as string) ?? 'V, S',
          duration: (e.data.duration as string) ?? 'Instantaneous',
          concentration: (e.data.concentration as boolean) ?? false,
          ritual: (e.data.ritual as boolean) ?? false,
          classes: (e.data.classes as string[]) ?? [],
          description: (e.data.description as string) ?? '',
        });
      }
    }
  }
  return out;
}

/**
 * Base SRD equipment + connected homebrew items, indexed by slug. Homebrew item
 * data nests armorData / weaponData whereas SrdEquipmentItem holds those fields
 * flat, so they are flattened here.
 */
export function mergedEquipmentMap(pkgs: HomebrewSessionPackage[]): Map<string, SrdEquipmentItem> {
  const out = new Map<string, SrdEquipmentItem>(srdEquipmentMap);
  for (const pkg of pkgs) {
    for (const e of pkg.entries) {
      if (e.type === 'item') {
        const armor = (e.data.armorData as Record<string, unknown> | undefined) ?? {};
        const weapon = (e.data.weaponData as Record<string, unknown> | undefined) ?? {};
        out.set(e.slug, {
          slug: e.slug,
          name: e.name,
          category: (e.data.category as string) ?? '',
          cost: (e.data.cost as string | null) ?? null,
          weight: (e.data.weight as number | null) ?? null,
          // Weapon (flattened from weaponData)
          weaponCategory: weapon.weaponCategory as string | undefined,
          weaponRange: weapon.weaponRange as string | undefined,
          damageDice: weapon.damageDice as string | undefined,
          damageType: weapon.damageType as string | null | undefined,
          twoHandedDice: weapon.twoHandedDice as string | undefined,
          rangeNormal: weapon.rangeNormal as number | null | undefined,
          rangeLong: weapon.rangeLong as number | null | undefined,
          properties: weapon.properties as string[] | undefined,
          throwRange: weapon.throwRange as number | undefined,
          throwRangeLong: weapon.throwRangeLong as number | undefined,
          // Armor (flattened from armorData)
          armorCategory: armor.armorCategory as string | undefined,
          acBase: armor.acBase as number | undefined,
          acDexBonus: armor.acDexBonus as boolean | undefined,
          acMaxBonus: armor.acMaxBonus as number | null | undefined,
          strMinimum: armor.strMinimum as number | undefined,
          stealthDisadvantage: armor.stealthDisadvantage as boolean | undefined,
          // Gear
          description: e.data.description as string | undefined,
        });
      }
    }
  }
  return out;
}

/**
 * Homebrew-merged SRD lists for Builder Mode. Reads the session overlay from
 * context (null outside a session — demo/tests → base SRD only).
 */
export function useBuilderSrd(): { classes: SrdClass[]; races: SrdRace[] } {
  const session = useOptionalSessionContext();
  const pkgs = session?.mp.serverState?.homebrewPackages;
  const classes = useMemo(() => mergedBuilderClasses(pkgs), [pkgs]);
  const races = useMemo(() => mergedBuilderRaces(pkgs), [pkgs]);
  return { classes, races };
}
